import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCheck, Trash2, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select } from "../../../components";
import {
  useMyNotifications,
  useMarkNotificationsAsRead,
  useRemoveNotification,
} from "../hooks";
import { notificationLink } from "../notification-routes";
import { formatDisplayDate } from "../../../helpers/date";

export const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const readFilter = searchParams.get("read");

  const where: any = {};
  if (readFilter === "false") where.read = false;
  if (readFilter === "true") where.read = true;

  const { data, loading } = useMyNotifications({ where });
  const { markAsRead, markAllAsRead } = useMarkNotificationsAsRead();
  const [remove] = useRemoveNotification();

  const notifications = data?.myNotifications?.results ?? [];
  const unreadCount = data?.myNotifications?.unreadCount ?? 0;

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (e.target.value) {
        next.set("read", e.target.value);
      } else {
        next.delete("read");
      }
      return next;
    });
  };

  const handleClick = (notification: any) => {
    if (!notification.read) {
      markAsRead([notification.id]);
    }
    const link = notificationLink(notification.table, notification.objectId);
    if (link) navigate(link);
  };

  const handleRemove = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette notification ?")) return;
    try {
      const { data } = await remove({ variables: { id } });
      if (!data?.removeNotification?.ok) {
        toast.error(data?.removeNotification?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  return (
    <>
      <Header
        title="Notifications"
        subtitle={
          unreadCount > 0
            ? `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}`
            : "Tout est à jour"
        }
      />

      <div className="main-container">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div className="w-full sm:w-56">
            <Select label="Affichage" className="w-full" value={readFilter ?? ""} onChange={handleFilterChange}>
              <option value="">Toutes</option>
              <option value="false">Non lues</option>
              <option value="true">Lues</option>
            </Select>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllAsRead()}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              <CheckCheck size={16} />
              Tout marquer comme lu
            </button>
          )}
        </div>

        <div className="card">
          {loading && !notifications.length ? (
            <Loading />
          ) : !notifications.length ? (
            <EmptyList text="Aucune notification" />
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-zinc-800">
              {notifications.map((notification) => {
                const link = notificationLink(
                  notification.table,
                  notification.objectId
                );
                return (
                  <div
                    key={`notification-row-${notification.id}`}
                    onClick={() => handleClick(notification)}
                    className={`flex items-start justify-between gap-3 py-3 px-1 ${
                      link ? "cursor-pointer" : ""
                    } ${
                      notification.read ? "" : "bg-indigo-50/40 dark:bg-indigo-950/10"
                    }`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      {!notification.read && (
                        <span className="mt-2 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p
                          className={`text-sm ${
                            notification.read
                              ? "text-gray-700 dark:text-zinc-300"
                              : "font-semibold text-gray-900 dark:text-zinc-100"
                          }`}
                        >
                          {notification.title}
                          {link && (
                            <ExternalLink
                              size={12}
                              className="inline ml-1.5 text-gray-400"
                            />
                          )}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-zinc-400 whitespace-pre-line">
                          {notification.content}
                        </p>
                        <p className="text-[10px] text-gray-400 dark:text-zinc-500 mt-0.5">
                          {formatDisplayDate(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleRemove(e, notification.id)}
                      title="Supprimer"
                      className="text-gray-400 hover:text-red-600 flex-shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
